import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import ButtonTabs from 'shared/components/tabs/tabs';
import { numberFormat } from 'shared/helpers';
import LineChart, { IChartData } from 'shared/components/chart/linechart';
import httpService from 'shared/services/http.service';
import { IconTripleDots } from 'shared/icons/icons';
import { Loader } from 'shared/components/spinner/spinner';
import { getMinSecDuration } from 'shared/util/utility';
import {
	AvgTimeGraph,
	DateLabelMapper,
	DetailsActions,
	GraphPreTitle,
	GraphTitle,
	IDashboardDetailsMapper,
	dashboardTabs
} from '../interface/dashboard';

const Dashboard: FC = () => {
	const [dashboardStats, setDashboardStats] = useState<IDashboardDetailsMapper>();
	const [checkChartEmpty, setCheckChartEmpty] = useState<any>({});
	const [activeDashboardTab, setActiveDashboardTab] = useState('all');
	const [dashboardDataLoader, setDashboardDataLoader] = useState(false);

	const dashboardGraphData = useMemo(() => {
		if (!isEmpty(dashboardStats)) {
			return Object.keys(dashboardStats);
		} else {
			return [];
		}
	}, [dashboardStats]);

	const getDashboardGraphData = useCallback(async (filterType: string) => {
		let graphDetails: any = {};
		const checkEmpty = {};
		setDashboardDataLoader(true);
		for (const key in DetailsActions) {
			const graph = DetailsActions[key];
			try {
				const GRAPH_URL = `dashboard/${graph}`;
				const response = await httpService.get(GRAPH_URL, { filterType });
				if (response) {
					if (
						!('performanceGraph' in response) ||
						('performanceGraph' in response && response.performanceGraph.some((ele) => ele.count === 0)) ||
						('performanceGraph' in response && response.performanceGraph.length === 0)
					) {
						checkEmpty[graph] = true;
					}
					graphDetails = {
						...graphDetails,
						[graph]: response
					};
				}
			} catch (error) {
				setDashboardDataLoader(false);
				console.error(error);
			}
		}
		setDashboardDataLoader(false);
		setCheckChartEmpty(checkEmpty);
		setDashboardStats({ ...graphDetails });
	}, []);

	useEffect(() => {
		getDashboardGraphData(activeDashboardTab);
	}, [getDashboardGraphData, activeDashboardTab]);

	const formatDate = (date: Date) => {
		const inputDate = date;

		// Parse the date
		const parsedDate = moment(inputDate);
		// Format the day with 'Do' for the ordinal suffix (e.g., 1st, 2nd, 3rd, 4th, etc.)
		const formattedDay = parsedDate.format(DateLabelMapper[activeDashboardTab][0]);

		// Format the month with 'MMM' for the abbreviated month name (e.g., Dec)
		const formattedMonth = parsedDate.format(DateLabelMapper[activeDashboardTab][1]);

		const formattedYear = parsedDate.format(DateLabelMapper[activeDashboardTab][2]);

		// Combine the formatted day and month
		const formattedDate =
			activeDashboardTab === 'day'
				? `${formattedDay}:${formattedMonth}:${formattedYear}`
				: `${formattedDay} ${formattedMonth} ${formattedYear}`;

		return formattedDate;
	};

	const createChartData = (key: string) => {
		return {
			labels: dashboardStats[key]?.performanceGraph.map((ele) => formatDate(ele.timestamp)),
			datasets: [
				{
					label: key,
					data: dashboardStats[key]?.performanceGraph.map((ele) =>
						key === AvgTimeGraph ? Math.round(ele.count / 60) : ele.count
					),
					borderColor: ['rgba(88, 201, 71, 1)'],
					backgroundColor: 'rgba(88, 201, 71, 1)',
					radius: 2,
					borderWidth: 2,
					tension: 0.5,
					fill: false // To remove the area under the line
				}
			]
		};
	};

	const options = {
		plugins: {
			legend: {
				display: false
			},
			tooltip: {
				enabled: true,
				callbacks: {
					label: (ctx) => {
						const raw = ctx.parsed.y;
						if (ctx.dataset.label === AvgTimeGraph) {
							const mins = Math.round(Number(raw || 0));
							return ` ${mins} min`;
						}
						return ` ${numberFormat(Number(raw || 0), 1)}`;
					}
				}
			}
		},
		interaction: { mode: 'index', intersect: false },
		elements: {
			point: { radius: 2, hoverRadius: 4 }
		},
		scales: {
			y: {
				beginAtZero: true,
				grid: { display: false },
				ticks: {
					callback: function (val, index, vals) {
						return index === 0 || index === vals.length - 1 ? this.getLabelForValue(val) : '';
					}
				}
			},
			x: {
				grid: { display: false },
				ticks: {
					callback: function (val, index, vals) {
						return index === 0 || index === vals.length - 1 ? this.getLabelForValue(val) : '';
					},
					maxRotation: 0,
					minRotation: 0
				}
			},
			r: { display: false }
		},
		maintainAspectRatio: false
	};

	const getPerfColor = (val?: number) => {
		if (!val) return '#6b7280';
		if (val > 0) return '#16a34a';
		if (val < 0) return '#dc2626';
		return '#6b7280';
	};

	const handleTabChange = (activeTab: string) => {
		setActiveDashboardTab(activeTab);
		setCheckChartEmpty({});
	};
	return (
		<>
			<header
				className='dashboard-header'
				style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}
			>
				<div>
					<h1 style={{ margin: 0, fontSize: 24 }}>Performance Dashboard</h1>
					<p style={{ margin: 0, opacity: 0.7 }}>
						Overview of key metrics · View: {activeDashboardTab.toUpperCase()}
					</p>
				</div>
				<div className='dashboard-statistics-tabs'>
					<ButtonTabs activeTab={activeDashboardTab} setActiveTab={handleTabChange} tabs={dashboardTabs} />
				</div>
			</header>
			{dashboardDataLoader ? (
				<Loader className='custom-loader dashboard-loader' />
			) : (
				<section className='dashboard-graph-wrapper'>
					<div className='graph-detail-data'>
						<ul
							className='graph-ul'
							style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
								gap: 24
							}}
						>
							{dashboardGraphData.map((graph, index) => {
								const total = dashboardStats?.[graph.toLocaleLowerCase()]?.total;
								const performancePer = dashboardStats?.[graph.toLocaleLowerCase()]?.performancePer;
								return (
									<li
										className='graph-li'
										key={index}
										style={{
											background: '#fff',
											borderRadius: 12,
											boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
											padding: 16
										}}
									>
										<div className='dashboard-graph-title'>
											<h4 className='graph-title'>
												{`${GraphPreTitle[activeDashboardTab]} ${GraphTitle[graph]}`}
											</h4>
										</div>
										<div className='dashboard-statistics'>
											<div className='dashboard-progress-data'>
												<div className='dashboard-personal-detail'>
													<div className='followers-data'>
														<h2 className='user-progress'>
															{graph === AvgTimeGraph
																? `${getMinSecDuration(total, 'HH:MM')} h`
																: `${numberFormat(total || 0, 1)}`}
														</h2>

														{activeDashboardTab !== 'all' && (
															<h6
																className='user-rate'
																style={{
																	color: getPerfColor(performancePer as number)
																}}
															>
																{`${numberFormat(
																	performancePer || 0,
																	1
																)}% from last ${activeDashboardTab}`}
															</h6>
														)}
													</div>
												</div>
											</div>
											<div className='graph-icon d-flex justify-content-end'>
												{checkChartEmpty[graph] ? (
													<p
														className='empty-chart'
														style={{
															margin: 0,
															padding: '24px 0',
															textAlign: 'center',
															color: '#9ca3af',
															border: '2px dashed #e5e7eb',
															borderRadius: 8
														}}
													>
														No data for the selected period
													</p>
												) : (
													<LineChart
														data={createChartData(graph) as unknown as IChartData}
														option={{
															...options,
															scales: {
																...options.scales,
																y: {
																	...options.scales.y,
																	title: {
																		display: true,
																		text:
																			graph === AvgTimeGraph ? 'Minutes' : 'Count'
																	}
																}
															}
														}}
													/>
												)}
											</div>
										</div>
									</li>
								);
							})}
						</ul>
					</div>
				</section>
			)}
		</>
	);
};

export default Dashboard;
